import SortIcon from 'assets/img/icons/caret-down.svg?react';
import { Box, Button, IconButton, styled, Typography, typographyClasses } from '@mui/material';
import React from 'react';
import type { LevelTableColumn } from 'redesign';
import { IconAccessTime, IconWarning } from 'assets/img';
import { ContextMenuButton, LevelTableHeaderProps, LwPopover } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { DateTime } from 'luxon';
import {
  PaymentOrderBy,
  PaymentsCellProp,
  PaymentsColumnDefinition,
  PaymentsTableOptions,
} from './payments.types';

export const getPaymentColumnDefinition: (
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions>[] = (t) => {
  const workerName: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    Header: () => (
      <Header>{t('payment.table_headers.flexworker_name', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'workerName',
    Cell: WorkerCell,
  };

  const paymentId: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    Header: () => (
      <Header>{t('payment.table_headers.payment_id', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'paymentId',
    Cell: PaymentIdCell,
  };

  const creationDate: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    id: PaymentOrderBy.creationDate,
    Header: (cell: LevelTableHeaderProps<PaymentsColumnDefinition, PaymentsTableOptions>) =>
      SortablePaymentHeaderCell(cell, t),
    accessor: 'creationDate',
    width: '150px',
    Cell: DateCell,
  };

  const invoice: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    id: PaymentOrderBy.invoice,
    Header: (cell: LevelTableHeaderProps<PaymentsColumnDefinition, PaymentsTableOptions>) =>
      SortablePaymentHeaderCell(cell, t),
    accessor: 'invoice',
    width: '150px',
    Cell: InvoiceCell,
  };

  const failureReason: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    accessor: 'failureReason',
    width: '50px',
    Cell: FailReasonCell(t),
  };

  const status: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    Header: () => (
      <Header>{t('payment.table_headers.status', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'status',
    width: '150px',
    Cell: PaymentStatusCell,
  };

  const dueDate: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    Header: () => (
      <Header>{t('payment.table_headers.due_date', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'dueDate',
    width: '150px',
    Cell: DateCell,
  };

  const actions: LevelTableColumn<PaymentsColumnDefinition, PaymentsTableOptions> = {
    Header: () => (
      <Header>{t('payment.table_headers.actions', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'actions',
    width: '150px',
    Cell: ActionCell,
  };

  return [workerName, paymentId, creationDate, invoice, failureReason, status, dueDate, actions];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const WorkerCell = (cell: PaymentsCellProp<'workerName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const PaymentIdCell = (cell: PaymentsCellProp<'paymentId'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const DateCell = (cell: PaymentsCellProp<'creationDate' | 'dueDate'>) => {
  {
    return (
      <BoxInfo>
        <Typography variant="body1" color="secondary">
          {cell.value ? cell.value.toFormat('dd MMM yyyy') : null}
        </Typography>
        <BoxTime>
          <IconAccessTime />
          <Typography variant="body1">
            {cell.value ? cell.value.toFormat('HH:mm') : null}
          </Typography>
        </BoxTime>
      </BoxInfo>
    );
  }
};
const InvoiceCell = (cell: PaymentsCellProp<'invoice'>) => {
  return (
    <Box>
      {cell.value ? (
        <a href={cell.value.invoiceUrl} download>
          <Typography>{cell.value.invoiceReference}</Typography>
        </a>
      ) : null}
    </Box>
  );
};

const FailReasonCell =
  (t: TFunctionNonStrict<'hq-screen-specific', undefined>) =>
  (cell: PaymentsCellProp<'failureReason'>) => {
    const { reason, date } = cell.value || {};

    if (!reason || !date) {
      return null;
    }

    const formattedDate = DateTime.fromISO(date).toFormat('dd MMMM yyyy, HH:mm');

    return (
      <LwPopover
        triggerButton={({ onClick }) => (
          <IconButton aria-label="failure-reason" data-testid="failure-icon" onClick={onClick}>
            <StyledIcon as={IconWarning} />
          </IconButton>
        )}
        id="failure-reason-popover"
      >
        <StyledPopoverContainer>
          <StyledFailedReasonTitle>
            {t('payment.failureReason.title', { ns: 'hq-screen-specific' })}
          </StyledFailedReasonTitle>
          <StyledReason data-testid="failure-reason">{reason}</StyledReason>
          <StyledDate data-testid="failure-date">{formattedDate}</StyledDate>
        </StyledPopoverContainer>
      </LwPopover>
    );
  };

const PaymentStatusCell = (cell: PaymentsCellProp<'status'>) => {
  return <Typography variant="body1">{cell.value}</Typography>;
};

const ActionCell = (cell: PaymentsCellProp<'actions'>) => {
  const actions = cell.getRowActions(cell.value.paymentId);
  return <ContextMenuButton>{actions}</ContextMenuButton>;
};

const SortablePaymentHeaderCell = (
  cell: LevelTableHeaderProps<PaymentsColumnDefinition, PaymentsTableOptions>,
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => {
  let headerText = '';

  switch (cell.column.id) {
    case PaymentOrderBy.creationDate:
      headerText = t('payment.table_headers.creation_date', { ns: 'hq-screen-specific' });
      break;
    case PaymentOrderBy.invoice:
      headerText = t('payment.table_headers.invoice', { ns: 'hq-screen-specific' });
      break;
    default:
      return null;
  }

  const SelectedComponent =
    cell.orderBy === cell.column.id ? StyledSortIconSelected : StyledSortIcon;

  const transformData =
    cell.orderBy === cell.column.id && cell.sortState === 'asc' ? 'rotate(180deg)' : '';

  return (
    <Button
      variant="text"
      onClick={() => {
        cell.onSortClick(cell);
      }}
    >
      <Header>{headerText}</Header>
      <SelectedComponent
        style={{
          transform: transformData,
        }}
      />
    </Button>
  );
};

const BoxInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
  alignItems: 'flexStart',
}));

const BoxTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  gap: theme.spacing(0.5),
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const StyledSortIcon = styled(SortIcon)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  height: '12px',
  width: '12px',
  transition: 'ease transform .5s',
}));

const StyledSortIconSelected = styled(SortIcon)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  transition: 'ease transform .5s',
  height: '16px',
  width: '16px',
  padding: '1px',
  border: `1px solid ${theme.palette.lwBlack[60]}`,
  borderRadius: '50%',
  boxSizing: 'border-box',
}));

const StyledFailedReasonTitle = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(18),
    marginBottom: theme.spacing(5),
  },
}));

const StyledReason = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontSize: theme.typography.pxToRem(15),
    marginBottom: theme.spacing(1),
  },
}));

const StyledDate = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.lwBlack[40],
    fontWeight: 500,
  },
}));

const StyledPopoverContainer = styled(Box)(() => ({
  maxWidth: '320px',
}));
